// src/pages/VehicleV2/VehicleFuelListing.jsx

import React, { useState, useContext, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaPlus, FaSearch, FaTrash, FaSave, FaTimes, FaChevronDown, FaChevronUp } from "react-icons/fa";

const RATE_LIST_API = "https://nissi-india.com/api/fuelRateListing";
const RATE_API = "https://nissi-india.com/api/fuelRate";
const TYPE_API = "https://nissi-india.com/api/fuelType";

const toInt = (v, def = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.trunc(n) : def;
};

const toStr = (v, def = "") => {
  if (v === undefined || v === null) return def;
  return String(v).trim();
};

const toNum = (v, def = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : def;
};

const dateToYMD = (value) => {
  if (!value) return "";
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const formatDate = (v) => {
  if (!v) return "-";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v);
  return d.toLocaleDateString("en-GB");
};

const VehicleFuelListing = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;
  const tableHeaderClass = buttonClass;

  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const branchId = localStorage.getItem("branchId") || localStorage.getItem("branchid") || "";
  const branch = localStorage.getItem("branchType") || localStorage.getItem("branchtype") || "";
  const userId = localStorage.getItem("userId") || localStorage.getItem("loginuserid") || "";
  const loginUserId = sessionStorage.getItem("loginUserId") || userId || "";

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branch": branch,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branch, userId]
  );

  const canAdd = useMemo(() => {
    const v = sessionStorage.getItem("fuelrate_addrights");
    if (v === null) return true;
    return v === "1" || v === "true";
  }, []);

  const canDelete = useMemo(() => {
    const v = sessionStorage.getItem("fuelrate_deleterights");
    if (v === null) return true;
    return v === "1" || v === "true";
  }, []);

  const branchActive = useMemo(() => {
    const v = sessionStorage.getItem("BranchActive") || localStorage.getItem("BranchActive");
    if (v === null) return true;
    return v === "1" || v === "true";
  }, []);

  const [showForm, setShowForm] = useState(true);
  const [showFilters, setShowFilters] = useState(true);
  const [search, setSearch] = useState("");

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [total, setTotal] = useState(0);
  const [fuelTypes, setFuelTypes] = useState([]);

  const [fuelRateId, setFuelRateId] = useState(0);
  const [fuelTypeName, setFuelTypeName] = useState("");
  const [rate, setRate] = useState("");
  const [effectDate, setEffectDate] = useState(dateToYMD(new Date()));
  const isEdit = fuelRateId > 0;

  const totalPages = useMemo(
    () => (limit > 0 ? Math.max(1, Math.ceil(total / limit)) : 1),
    [total, limit]
  );

  const inputClass = `w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
    isDark
      ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
      : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
  }`;
  const labelClass = `block mb-2 text-sm font-medium ${isDark ? "text-gray-300" : "text-gray-700"}`;

  const loadFuelTypes = useCallback(async () => {
    try {
      const { data } = await axios.get(`${RATE_API}/fuel-types`, { headers: commonHeaders });
      setFuelTypes(Array.isArray(data?.fuelTypes) ? data.fuelTypes : []);
    } catch (_e) {
      setFuelTypes([]);
    }
  }, [commonHeaders]);

  const fetchList = useCallback(
    async (opts = {}) => {
      const nextPage = opts.page ?? page;
      const nextLimit = opts.limit ?? limit;
      const nextSearch = opts.search ?? search;

      try {
        setLoading(true);
        const { data } = await axios.get(`${RATE_LIST_API}/list`, {
          params: { loginUserId, search: nextSearch, page: nextPage, limit: nextLimit },
          headers: commonHeaders,
        });
        if (!data?.success) throw new Error(data?.message || "Failed to load list");

        const nextRows = Array.isArray(data.rows) ? data.rows : [];
        const nextTotal = toInt(data.total, 0);
        setRows(nextRows);
        setTotal(nextTotal);
        setSelectedIds([]);

        const computed = nextLimit > 0 ? Math.max(1, Math.ceil(nextTotal / nextLimit)) : 1;
        if (nextPage > computed) {
          setPage(computed);
          return fetchList({ page: computed, limit: nextLimit, search: nextSearch });
        }
        setPage(nextPage);
      } catch (err) {
        toast.error(err?.response?.data?.message || err?.message || "Server error");
        setRows([]);
        setTotal(0);
        setSelectedIds([]);
      } finally {
        setLoading(false);
      }
    },
    [page, limit, search, commonHeaders, loginUserId]
  );

  useEffect(() => {
    loadFuelTypes();
    fetchList({ page: 1 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [commonHeaders]);

  const resetForm = () => {
    setFuelRateId(0);
    setFuelTypeName("");
    setRate("");
    setEffectDate(dateToYMD(new Date()));
  };

  const ensureFuelTypeId = async (name) => {
    const typed = toStr(name);
    if (!typed) throw new Error("Please enter Fuel Type Name");

    let types = fuelTypes;
    if (!types.length) {
      const { data } = await axios.get(`${RATE_API}/fuel-types`, { headers: commonHeaders });
      types = Array.isArray(data?.fuelTypes) ? data.fuelTypes : [];
      setFuelTypes(types);
    }

    const found = types.find(
      (x) => toStr(x.name).toLowerCase() === typed.toLowerCase()
    );
    if (found) return toInt(found.id);

    const { data } = await axios.post(
      `${TYPE_API}/save`,
      { fuelTypeId: 0, fuelTypeName: typed },
      { headers: commonHeaders }
    );
    if (!data?.success) throw new Error(data?.message || "Failed to save fuel type");

    const createdId = toInt(data?.fuelTypeId || data?.id || data?.data?.fuelTypeId, 0);
    if (createdId > 0) {
      await loadFuelTypes();
      return createdId;
    }

    const again = await axios.get(`${RATE_API}/fuel-types`, { headers: commonHeaders });
    const nextTypes = Array.isArray(again.data?.fuelTypes) ? again.data.fuelTypes : [];
    setFuelTypes(nextTypes);
    const match = nextTypes.find((x) => toStr(x.name).toLowerCase() === typed.toLowerCase());
    if (!match) throw new Error("Fuel type saved but id not found. Please try again.");
    return toInt(match.id);
  };

  const handleSave = async () => {
    if (!branchActive || !canAdd) return toast.error("Save not allowed");
    if (!toStr(fuelTypeName)) return toast.error("Please enter Fuel Type Name");
    if (!toStr(rate)) return toast.error("Please enter Fuel Rate");

    try {
      setSaving(true);
      const typeId = await ensureFuelTypeId(fuelTypeName);

      const { data } = await axios.post(
        `${RATE_API}/save`,
        {
          fuelRateId: fuelRateId || 0,
          fuelTypeId: typeId,
          rate: toStr(rate),
          effectDate: effectDate || "",
          loginUserId,
        },
        { headers: commonHeaders }
      );
      if (!data?.success) throw new Error(data?.message || "Failed to save");

      toast.success(data?.message || (isEdit ? "Updated successfully" : "Saved successfully"));
      resetForm();
      await loadFuelTypes();
      await fetchList({ page });
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = async (row) => {
    const id = toInt(row?.fuelRateId);
    if (!id) return;
    setShowForm(true);
    setFuelRateId(id);
    setFuelTypeName(toStr(row?.fuelTypeName));
    setRate(row?.rate != null ? String(row.rate) : "");
    setEffectDate(dateToYMD(row?.date || row?.effectDate));

    try {
      const { data } = await axios.get(`${RATE_API}/view`, {
        params: { fuelRateId: id },
        headers: commonHeaders,
      });
      const d = data?.data || {};
      setFuelTypeName(toStr(d.fuelTypeName || row?.fuelTypeName));
      setRate(d.rate != null ? String(d.rate) : String(row?.rate || ""));
      setEffectDate(dateToYMD(d.effectDate || d.date || row?.date));
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Failed to load");
    }
  };

  const handleDelete = async () => {
    if (!branchActive || !canDelete) return;
    if (!selectedIds.length) return toast.warn("Select at least one record");
    if (!window.confirm(`Delete ${selectedIds.length} selected record(s)?`)) return;

    try {
      setLoading(true);
      const { data } = await axios.post(
        `${RATE_LIST_API}/delete`,
        { ids: selectedIds, loginUserId },
        { headers: commonHeaders }
      );
      if (!data?.success) throw new Error(data?.message || "Delete failed");
      toast.success(data?.message || "Deleted successfully");
      if (selectedIds.includes(fuelRateId)) resetForm();
      const remaining = Math.max(0, total - selectedIds.length);
      const remainingPages = limit > 0 ? Math.max(1, Math.ceil(remaining / limit)) : 1;
      await fetchList({ page: Math.min(page, remainingPages) });
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Delete failed");
    } finally {
      setLoading(false);
    }
  };

  const idsOnPage = rows.map((x) => toInt(x.fuelRateId)).filter((x) => x > 0);
  const allSelected = idsOnPage.length > 0 && idsOnPage.every((id) => selectedIds.includes(id));

  return (
    <VehicleLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">VEHICLE FUEL RATE</span>}
          setSkin={setSkin}
          currentSkin={currentSkin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
            <h2 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
              Vehicle Fuel Rate
            </h2>
            <div className="flex items-center gap-3 flex-wrap">
              
              <button
                onClick={() => setShowForm((p) => !p)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                    : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
                }`}
              >
                {showForm ? <FaChevronUp /> : <FaChevronDown />}
                {showForm ? "Hide Form" : "Show Form"}
              </button>
              <button
                onClick={() => setShowFilters((p) => !p)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                    : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
                }`}
              >
                {showFilters ? <FaChevronUp /> : <FaChevronDown />}
                {showFilters ? "Hide Search" : "Show Search"}
              </button>
            </div>
          </div>

          {showForm && (
            <div className={`p-6 rounded-lg mb-6 ${isDark ? "bg-gray-800/50" : "bg-blue-50"}`}>
              <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                <h3 className={`text-lg font-semibold ${isDark ? "text-white" : "text-blue-900"}`}>
                  {isEdit ? "Edit Fuel Rate" : "Add Fuel Rate"}
                </h3>
                <div className="flex gap-2">
                  <button
                    onClick={resetForm}
                    className={`px-4 py-2 rounded-lg text-sm font-semibold border ${
                      isDark
                        ? "border-gray-600 text-gray-200 hover:bg-gray-700"
                        : "border-gray-300 text-gray-700 hover:bg-white"
                    }`}
                  >
                    <span className="inline-flex items-center gap-2"><FaTimes /> Clear</span>
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={saving || !branchActive || !canAdd}
                    className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg text-white ${
                      saving ? "opacity-60 cursor-not-allowed" : ""
                    } ${buttonClass} ${hoverClass}`}
                  >
                    <FaSave />
                    {saving ? "Saving..." : isEdit ? "Update" : "Submit"}
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <label className={labelClass}>Fuel Type Name *</label>
                  <input
                    list="fuelTypeNameList"
                    value={fuelTypeName}
                    onChange={(e) => setFuelTypeName(e.target.value)}
                    className={inputClass}
                    placeholder="Enter fuel type name"
                    disabled={saving}
                  />
                  <datalist id="fuelTypeNameList">
                    {fuelTypes.map((x) => (
                      <option key={x.id} value={x.name} />
                    ))}
                  </datalist>
                </div>
                <div>
                  <label className={labelClass}>Fuel Rate *</label>
                  <input
                    value={rate}
                    onChange={(e) => setRate(e.target.value)}
                    className={inputClass}
                    placeholder="Enter rate"
                    disabled={saving}
                  />
                </div>
                <div>
                  <label className={labelClass}>Date</label>
                  <input
                    type="date"
                    value={effectDate}
                    onChange={(e) => setEffectDate(e.target.value)}
                    className={inputClass}
                    disabled={saving}
                  />
                </div>
              </div>
            </div>
          )}

          {showFilters && (
            <div className={`p-6 rounded-lg mb-6 ${isDark ? "bg-gray-800/50" : "bg-blue-50"}`}>
              <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                <h3 className={`text-lg font-semibold ${isDark ? "text-white" : "text-blue-900"}`}>
                  Search Criteria
                </h3>
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      setSearch("");
                      fetchList({ page: 1, search: "" });
                    }}
                    className={`px-4 py-2 rounded-lg text-sm font-semibold border ${
                      isDark
                        ? "border-gray-600 text-gray-200 hover:bg-gray-700"
                        : "border-gray-300 text-gray-700 hover:bg-white"
                    }`}
                  >
                    Clear
                  </button>
                  <button
                    onClick={() => fetchList({ page: 1, search })}
                    className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg text-white ${buttonClass} ${hoverClass}`}
                  >
                    <FaSearch /> Search
                  </button>
                </div>
              </div>
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && fetchList({ page: 1, search })}
                className={inputClass}
                placeholder="Search fuel type / rate..."
              />
            </div>
          )}

          <div className="overflow-x-auto rounded-lg border shadow-lg">
            {loading ? (
              <div className={`p-16 text-center ${isDark ? "bg-gray-800" : "bg-gray-50"}`}>Loading...</div>
            ) : rows.length === 0 ? (
              <div className={`p-16 text-center ${isDark ? "bg-gray-800 text-gray-400" : "bg-gray-50 text-gray-500"}`}>
                No records found
              </div>
            ) : (
              <>
                <table className={`w-full ${isDark ? "bg-gray-800" : "bg-white"}`}>
                  <thead className={`${tableHeaderClass} text-white`}>
                    <tr>
                      <th className="px-6 py-4 text-left text-sm font-semibold">S.No</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold">Fuel Type</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold">Fuel Rate</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold">Last Updated Date</th>
                      <th className="px-6 py-4 text-center text-sm font-semibold">Edit</th>
                      <th className="px-6 py-4 text-center text-sm font-semibold w-16">
                        <input
                          type="checkbox"
                          checked={allSelected}
                          onChange={() => setSelectedIds(allSelected ? [] : idsOnPage)}
                          className="w-4 h-4 cursor-pointer"
                        />
                      </th>
                    </tr>
                  </thead>
                  <tbody className={`${isDark ? "divide-gray-700" : "divide-gray-200"} divide-y`}>
                    {rows.map((r, idx) => {
                      const id = toInt(r.fuelRateId);
                      return (
                        <tr
                          key={id || idx}
                          className={`${
                            idx % 2 === 0
                              ? isDark ? "bg-gray-800" : "bg-white"
                              : isDark ? "bg-gray-750" : "bg-gray-50"
                          } ${fuelRateId === id ? (isDark ? "ring-1 ring-blue-400" : "bg-blue-50") : ""} ${
                            isDark ? "hover:bg-gray-700" : "hover:bg-blue-50"
                          }`}
                        >
                          <td className="px-6 py-4 text-sm">{r.slno ?? (page - 1) * limit + idx + 1}</td>
                          <td className={`px-6 py-4 text-sm font-medium ${isDark ? "text-blue-400" : "text-blue-600"}`}>
                            {r.fuelTypeName || "-"}
                          </td>
                          <td className="px-6 py-4 text-sm">{toNum(r.rate, 0).toFixed(2)}</td>
                          <td className="px-6 py-4 text-sm">{formatDate(r.date || r.effectDate || r.updatedDate)}</td>
                          <td className="px-6 py-4 text-center">
                            <button
                              type="button"
                              onClick={() => handleEdit(r)}
                              className="text-yellow-500 hover:text-yellow-600 text-lg"
                              title="Edit"
                            >
                              ✏️
                            </button>
                          </td>
                          <td className="px-6 py-4 text-center">
                            <input
                              type="checkbox"
                              checked={selectedIds.includes(id)}
                              onChange={() =>
                                setSelectedIds((p) =>
                                  p.includes(id) ? p.filter((x) => x !== id) : [...p, id]
                                )
                              }
                              className="w-4 h-4 cursor-pointer"
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                {canDelete && branchActive && (
                  <div className="p-4">
                    <button
                      onClick={handleDelete}
                      disabled={!selectedIds.length}
                      className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold shadow text-white ${
                        selectedIds.length === 0
                          ? "bg-gray-400 cursor-not-allowed"
                          : "bg-red-600 hover:bg-red-700"
                      }`}
                    >
                      <FaTrash /> Remove
                    </button>
                  </div>
                )}
              </>
            )}
          </div>

          <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-3 mt-4">
            <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-700"}`}>
              Page <b>{page}</b> of <b>{totalPages}</b> | Total: <b>{total}</b>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <select
                value={limit}
                onChange={(e) => {
                  const nl = toInt(e.target.value, 25);
                  setLimit(nl);
                  fetchList({ page: 1, limit: nl });
                }}
                className={inputClass.replace("w-full ", "w-auto ")}
              >
                {[10, 25, 50, 100].map((n) => (
                  <option key={n} value={n}>{n} / page</option>
                ))}
              </select>
              <button
                disabled={page <= 1}
                onClick={() => fetchList({ page: page - 1 })}
                className={`px-3 py-2 border rounded-lg ${page <= 1 ? "opacity-50 cursor-not-allowed" : ""} ${
                  isDark ? "border-gray-600 text-white" : "border-gray-300 text-gray-700"
                }`}
              >
                Prev
              </button>
              <button
                disabled={page >= totalPages}
                onClick={() => fetchList({ page: page + 1 })}
                className={`px-3 py-2 border rounded-lg ${
                  page >= totalPages ? "opacity-50 cursor-not-allowed" : ""
                } ${isDark ? "border-gray-600 text-white" : "border-gray-300 text-gray-700"}`}
              >
                Next
              </button>
            </div>
          </div>

          <ToastContainer />
        </div>
      </div>
    </VehicleLayoutWrapper>
  );
};

export default VehicleFuelListing;